import { amplitudeLib, amplitudeExperiment } from 'freedom-shared';

/**
 * Вариант A/B `avfree_factoring_on`, приклеенный к каждому событию рассрочки.
 * Читается вне реактивного контекста: события уходят и со страниц, где гейт
 * фичи не вызывался (скоринг, экран результата). `control` вместо пустого
 * значения — иначе в Amplitude контрольная группа неотличима от события
 * старой версии приложения.
 */
export const getFactoringVariant = () =>
  amplitudeExperiment.lib.getCurrentVariant(
    amplitudeExperiment.config.flags.FACTORING,
  ) ?? 'control';

/**
 * Идентификаторы заявки. Кладутся в каждое событие рассрочки: обращение в
 * саппорт приходит с номером заказа, и по нему нужно поднять весь путь клиента
 * до банка, а инцидент в Sentry — сопоставить с конкретным счётом.
 */
export type FactoringContext = {
  order_id?: string;
  bill_id?: string;
  provider_id?: string;
  /** Срок, выбранный на момент события (месяцы). */
  installment_term?: number;
  /** Сумма счёта, ₸. */
  amount?: number;
  /** Заявка в банке — появляется на скоринге. */
  proposal_id?: string;
};

/** Шаг воронки, на котором всё сломалось. */
export type FactoringErrorStage =
  | 'start'
  | 'docs'
  | 'otp_send'
  | 'otp_check'
  | 'prescoring'
  | 'scoring_start'
  | 'scoring_polling'
  | 'result';

export type FactoringOtpErrorReason =
  | 'invalid_code'
  | 'expired'
  | 'throttled'
  | 'attempts_exhausted'
  | 'request_failed';

/** Чем закончился прескоринг с точки зрения клиента. */
export type FactoringPrescoringOutcome =
  | 'offers'
  | 'rejected'
  | 'limit_insufficient';

const logFactoringEvent = (
  event: string,
  params: FactoringContext & Record<string, unknown> = {},
) =>
  amplitudeLib.logEvent(event, {
    ...params,
    factoring_variant: getFactoringVariant(),
  });

export type FactoringMethodChangeParams = FactoringContext & {
  method: string;
  /** Способ, с которого ушли, — половина отказов от рассрочки видна только так. */
  previous_method?: string;
};

export type FactoringTermSelectedParams = FactoringContext & {
  previous_term?: number;
};

export type FactoringEventParams = FactoringContext;

export type FactoringStartParams = FactoringContext & {
  /** Заявка уже подписана — документы и SMS пропускаются. */
  is_signed: boolean;
};

export type FactoringRejectedParams = FactoringContext & {
  reject_reason?: string;
};

export type FactoringErrorParams = FactoringContext & {
  stage: FactoringErrorStage;
  error?: string;
};

export type FactoringOtpSentParams = FactoringContext & {
  /** Повторная отправка — клиент не получил или не успел ввести первый код. */
  is_resend: boolean;
};

export type FactoringOtpErrorParams = FactoringContext & {
  reason: FactoringOtpErrorReason;
  attempts_left?: number;
  /** Сколько секунд бэкенд просит подождать до следующей SMS (`throttled`). */
  wait_seconds?: number;
};

export type FactoringPrescoringResultParams = FactoringContext & {
  outcome: FactoringPrescoringOutcome;
  /** Статус с бэка как есть: `APPROVED` / `PARTIAL` / `REJECTED`. */
  status?: string;
  /** Одобренный лимит, ₸ — по нему разбирается «лимита не хватило». */
  limit?: number;
  offers_count: number;
};

export type FactoringScoringApprovedParams = FactoringContext & {
  /** Банк дал ссылку. `false` — одобрено, но ссылки за 40 попыток не пришло. */
  has_redirect: boolean;
};

export type FactoringResultShownParams = FactoringContext & {
  result: 'success' | 'error';
};

/** Блок способов оплаты с рассрочкой отрисован — знаменатель нижней воронки. */
export const factoringMethodShown = (params: FactoringContext) =>
  logFactoringEvent('avfree_factoring_method_shown', params);

export const paymentMethodChanged = (params: FactoringMethodChangeParams) =>
  logFactoringEvent('avfree_payment_method_changed', params);

/** Клиент сам переключил срок чипом; авто-подстановка срока сюда не попадает. */
export const factoringTermSelected = (params: FactoringTermSelectedParams) =>
  logFactoringEvent('avfree_factoring_term_selected', params);

/** Шторка согласия с документами открыта. */
export const factoringDocsShown = (params: FactoringEventParams) =>
  logFactoringEvent('avfree_factoring_docs_shown', params);

/** Согласие подтверждено — клиент уходит на подписание по SMS. */
export const factoringDocsConfirmed = (params: FactoringEventParams) =>
  logFactoringEvent('avfree_factoring_docs_confirmed', params);

/** Нажата оплата рассрочкой — вход в воронку факторинга. */
export const factoringStart = (params: FactoringStartParams) =>
  logFactoringEvent('avfree_factoring_start', params);

export const factoringOtpSent = (params: FactoringOtpSentParams) =>
  logFactoringEvent('avfree_factoring_otp_sent', params);

export const factoringOtpError = (params: FactoringOtpErrorParams) =>
  logFactoringEvent('avfree_factoring_otp_error', params);

/** Заявка подписана — своей подписью или уже подписанной ранее. */
export const factoringSigned = (
  params: FactoringEventParams & { was_already_signed: boolean },
) => logFactoringEvent('avfree_factoring_signed', params);

export const factoringPrescoringResult = (
  params: FactoringPrescoringResultParams,
) => logFactoringEvent('avfree_factoring_prescoring_result', params);

/** Выбран обязывающий срок, клиент уходит на скоринг. */
export const factoringPrescoringContinue = (params: FactoringEventParams) =>
  logFactoringEvent('avfree_factoring_prescoring_continue', params);

export const factoringScoringApproved = (
  params: FactoringScoringApprovedParams,
) => logFactoringEvent('avfree_factoring_scoring_approved', params);

export const factoringScoringRejected = (params: FactoringRejectedParams) =>
  logFactoringEvent('avfree_factoring_scoring_rejected', params);

/**
 * Последнее событие перед уходом на страницу банка. Дальше приложение теряет
 * клиента из виду, поэтому именно по нему разбирается «ушёл в банк и пропал».
 */
export const factoringBankRedirect = (params: FactoringEventParams) =>
  logFactoringEvent('avfree_factoring_bank_redirect', params);

/** Показан финальный экран рассрочки. */
export const factoringResultShown = (params: FactoringResultShownParams) =>
  logFactoringEvent('avfree_factoring_result_shown', params);

export const factoringError = (params: FactoringErrorParams) =>
  logFactoringEvent('avfree_factoring_error', params);
